import { useMemo } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import { useTheme } from '../../context/ThemeContext'
import { formatCurrency, formatMonthShort } from '../../utils/formatters'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

export default function LineChart({
  data,
  labels,
  datasets,
  yAxisPrefix = '$',
  fill = false,
  tension = 0.4,
  aspectRatio = 2
}) {
  const { isDark } = useTheme()

  const chartData = useMemo(() => ({
    labels: labels.map(formatMonthShort),
    datasets: datasets.map((ds, i) => ({
      label: ds.label,
      data: ds.data,
      borderColor: ds.color || `hsl(${i * 60}, 70%, 50%)`,
      backgroundColor: fill
        ? `${ds.color || `hsl(${i * 60}, 70%, 50%)`}20`
        : 'transparent',
      fill: fill,
      tension: tension,
      pointRadius: 3,
      pointHoverRadius: 6,
      borderWidth: 2
    }))
  }), [labels, datasets, fill, tension])

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        display: datasets.length > 1,
        position: 'top',
        labels: {
          color: isDark ? '#9ca3af' : '#6b7280',
          usePointStyle: true,
          padding: 20
        }
      },
      tooltip: {
        backgroundColor: isDark ? '#1f2937' : '#ffffff',
        titleColor: isDark ? '#f3f4f6' : '#111827',
        bodyColor: isDark ? '#d1d5db' : '#4b5563',
        borderColor: isDark ? '#374151' : '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        callbacks: {
          label: function(context) {
            const value = context.parsed.y
            const formatted = yAxisPrefix === '$'
              ? formatCurrency(value)
              : value.toLocaleString()
            return `${context.dataset.label}: ${formatted}`
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          color: isDark ? '#374151' : '#f3f4f6'
        },
        ticks: {
          color: isDark ? '#9ca3af' : '#6b7280'
        }
      },
      y: {
        grid: {
          color: isDark ? '#374151' : '#f3f4f6'
        },
        ticks: {
          color: isDark ? '#9ca3af' : '#6b7280',
          callback: function(value) {
            if (yAxisPrefix === '$') {
              return formatCurrency(value, { compact: true })
            }
            return value.toLocaleString()
          }
        }
      }
    }
  }), [isDark, datasets.length, yAxisPrefix, aspectRatio])

  return (
    <div className="w-full h-full">
      <Line data={chartData} options={options} />
    </div>
  )
}
